import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Activity,
  CalendarDays,
  Clock3,
  History,
  Monitor,
  Power,
  RefreshCw,
  Search,
  ShieldCheck,
  Smartphone,
  TimerReset,
  UserRoundCheck,
  Users,
  Wifi,
  WifiOff,
  X,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageShell } from "@/pages/dashboard/RolePages";
import { useAuth } from "@/context/AuthContext";

const ROLE_LABELS = {
  user: "Usuario",
  student: "Estudiante",
  consultant: "Consultor",
  creator: "Creador",
  support: "Soporte",
  admin: "Administrador",
  director: "Director",
};

const STATE_META = {
  active: { label: "Activo", symbol: "●", color: "#86EFAC", bg: "rgba(34,197,94,.10)" },
  idle: { label: "Inactivo", symbol: "●", color: "#FACC15", bg: "rgba(250,204,21,.10)" },
  offline: { label: "Desconectado", symbol: "●", color: "var(--kd9-text-soft)", bg: "rgba(148,163,184,.08)" },
};

const MODULE_LABELS = {
  inicio: "Inicio",
  diagnosticos: "Diagnósticos",
  academia: "Academia",
  tienda: "Marketplace",
  "energia-kd": "Energía KD",
  "templo-vivo": "Templo Vivo",
  agenda: "Agenda",
};

const formatDate = (value) => {
  if (!value) return "Sin registro";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Sin registro";
  return new Intl.DateTimeFormat("es-CO", {
    dateStyle: "short",
    timeStyle: "short",
    timeZone: "America/Bogota",
  }).format(date);
};

const formatDuration = (seconds) => {
  const total = Math.max(0, Number(seconds) || 0);
  if (total < 60) return total ? `${total}s` : "0 min";
  const minutes = Math.floor(total / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours < 24) return rest ? `${hours}h ${rest}m` : `${hours}h`;
  const days = Math.floor(hours / 24);
  const restHours = hours % 24;
  return restHours ? `${days}d ${restHours}h` : `${days}d`;
};

const roleLabel = (row) => {
  const roles = row?.roles || [];
  const preferred = ["director", "admin", "support", "consultant", "creator", "student", "user"];
  const role = preferred.find((item) => roles.includes(item)) || row?.role_base || "user";
  return ROLE_LABELS[role] || role;
};

const moduleLabel = (value) => {
  if (!value) return "Sin módulo";
  return MODULE_LABELS[value] || String(value).replaceAll("-", " ");
};

const StateBadge = ({ state }) => {
  const meta = STATE_META[state] || STATE_META.offline;
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-mont text-[10px] uppercase tracking-wider"
      style={{ color: meta.color, background: meta.bg, border: `1px solid ${meta.color}33` }}
    >
      <span aria-hidden="true">{meta.symbol}</span>
      {meta.label}
    </span>
  );
};

const Filters = ({ filters, setFilters, support = false }) => (
  <div className="kd9-card p-4 mb-5 grid gap-3 md:grid-cols-2 xl:grid-cols-5" data-testid={support ? "session-support-filters" : "activity-overview-filters"}>
    <label className="xl:col-span-2">
      <span className="sr-only">Buscar usuario</span>
      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--kd9-text-soft)" }} />
        <input
          value={filters.search}
          onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))}
          placeholder="Buscar por nombre, correo o ID KD9"
          className="w-full rounded-xl border bg-transparent py-2.5 pl-9 pr-3 font-mont text-xs outline-none"
          style={{ borderColor: "var(--kd9-border)", color: "var(--kd9-text)" }}
        />
      </div>
    </label>
    <select
      value={filters.role}
      onChange={(event) => setFilters((current) => ({ ...current, role: event.target.value }))}
      className="rounded-xl border bg-transparent px-3 py-2.5 font-mont text-xs"
      style={{ borderColor: "var(--kd9-border)", color: "var(--kd9-text)", background: "var(--kd9-bg)" }}
      aria-label="Filtrar por rol"
    >
      <option value="">Todos los roles</option>
      {Object.entries(ROLE_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
    </select>
    <select
      value={filters.state}
      onChange={(event) => setFilters((current) => ({ ...current, state: event.target.value }))}
      className="rounded-xl border bg-transparent px-3 py-2.5 font-mont text-xs"
      style={{ borderColor: "var(--kd9-border)", color: "var(--kd9-text)", background: "var(--kd9-bg)" }}
      aria-label="Filtrar por actividad"
    >
      <option value="">Cualquier actividad</option>
      <option value="active">Activos ahora</option>
      <option value="idle">Inactivos en sesión</option>
      <option value="offline">Desconectados</option>
    </select>
    <select
      value={filters.status}
      onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))}
      className="rounded-xl border bg-transparent px-3 py-2.5 font-mont text-xs"
      style={{ borderColor: "var(--kd9-border)", color: "var(--kd9-text)", background: "var(--kd9-bg)" }}
      aria-label="Filtrar por estado de cuenta"
    >
      <option value="">Todas las cuentas</option>
      <option value="active">Cuenta activa</option>
      <option value="inactive">Cuenta inactiva</option>
      <option value="suspended">Cuenta suspendida</option>
    </select>
  </div>
);

const UserIdentity = ({ row }) => (
  <div className="min-w-0">
    <div className="font-mont text-xs font-semibold truncate">
      {[row.name, row.surname].filter(Boolean).join(" ") || "Usuario KD9"}
    </div>
    <div className="font-mont text-[10px] truncate" style={{ color: "var(--kd9-text-soft)" }}>{row.email}</div>
    {row.kd9_id && <div className="font-mono text-[9px] mt-0.5 kd9-gold-text">{row.kd9_id}</div>}
  </div>
);

const ErrorBanner = ({ error, onRetry }) => error ? (
  <div className="mb-5 rounded-xl border p-4 flex items-center gap-3" style={{ color: "#FCA5A5", borderColor: "rgba(122,14,23,.4)" }} role="alert">
    <span className="font-mont text-xs flex-1">{error}</span>
    <button type="button" onClick={onRetry} className="kd9-btn-outline px-3 py-2 rounded-lg text-[10px] inline-flex items-center gap-1.5">
      <RefreshCw size={12} /> Reintentar
    </button>
  </div>
) : null;

const ActivityCenter = () => {
  const { user, accessMap } = useAuth();
  const [filters, setFilters] = useState({ search: "", role: "", state: "", status: "" });
  const [data, setData] = useState({ summary: null, items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [revokingUserId, setRevokingUserId] = useState(null);
  const canManageSessions = useMemo(
    () => new Set(accessMap?.permissions || []).has("users.manage"),
    [accessMap]
  );

  const params = useMemo(() => {
    const result = { limit: 200 };
    Object.entries(filters).forEach(([key, value]) => {
      if (value) result[key] = value;
    });
    return result;
  }, [filters]);

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    setError("");
    try {
      const response = await api.get("/api/activity/overview", { params });
      setData(response.data || { summary: null, items: [], total: 0 });
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    const timer = window.setTimeout(() => load(), 250);
    return () => window.clearTimeout(timer);
  }, [load]);

  useEffect(() => {
    const interval = window.setInterval(() => load({ quiet: true }), 60_000);
    return () => window.clearInterval(interval);
  }, [load]);

  const revokeSessions = async (row) => {
    if (!canManageSessions || !row?.id || row.id === user?.id) return;
    const label = [row.name, row.surname].filter(Boolean).join(" ") || row.email || "este usuario";
    const confirmed = window.confirm(
      `¿Cerrar todas las sesiones activas de ${label}? Deberá iniciar sesión nuevamente.`
    );
    if (!confirmed) return;

    setRevokingUserId(row.id);
    setError("");
    setNotice("");
    try {
      const response = await api.post(`/api/activity/users/${row.id}/revoke-sessions`);
      const count = Number(response.data?.revoked_sessions || 0);
      setNotice(
        count
          ? `Se cerraron ${count} sesión${count === 1 ? "" : "es"} de ${label}.`
          : `${label} no tenía sesiones activas.`
      );
      await load({ quiet: true });
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setRevokingUserId(null);
    }
  };

  const summary = data.summary || {};
  const metrics = [
    ["registered_users", "Registrados", Users],
    ["connected_now", "Conectados ahora", Wifi],
    ["active_now", "Activos ahora", Activity],
    ["active_today", "Activos hoy", UserRoundCheck],
    ["active_7_days", "Activos 7 días", CalendarDays],
    ["active_30_days", "Activos 30 días", CalendarDays],
    ["inactive_30_days", "Sin entrar +30 días", WifiOff],
    ["sessions_today", "Sesiones de hoy", Clock3],
  ];

  return (
    <PageShell
      kicker="Dirección y Administración · Telemetría"
      title="ACTIVIDAD DE USUARIOS"
      desc="Uso real de KD9: sesiones, actividad efectiva, recurrencia reciente y último módulo utilizado."
      testId="activity-overview-page"
      badge={<span className="inline-flex items-center gap-2 text-[10px] font-mont uppercase tracking-widest kd9-gold-text"><ShieldCheck size={14}/> Datos operativos</span>}
    >
      <ErrorBanner error={error} onRetry={() => load()} />
      {notice && (
        <div className="mb-5 rounded-xl border p-4 font-mont text-xs" style={{ color: "#86EFAC", borderColor: "rgba(34,197,94,.35)", background: "rgba(34,197,94,.06)" }} role="status">
          {notice}
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4" data-testid="activity-summary">
        {metrics.map(([key, label, Icon]) => (
          <div className="kd9-card p-4" key={key} data-testid={`activity-metric-${key}`}>
            <Icon size={17} className="kd9-gold-text mb-3" />
            <div className="font-cinzel text-2xl">{loading && !data.summary ? "…" : Number(summary[key] || 0).toLocaleString("es-CO")}</div>
            <div className="font-mont text-[9px] uppercase tracking-widest mt-1" style={{ color: "var(--kd9-text-soft)" }}>{label}</div>
          </div>
        ))}
      </div>

      <div className="kd9-card p-4 mb-5 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <TimerReset size={17} className="kd9-gold-text" />
          <div>
            <div className="font-mont text-[9px] uppercase tracking-widest" style={{ color: "var(--kd9-text-soft)" }}>Promedio activo / sesión · 30 días</div>
            <div className="font-cinzel text-lg">{formatDuration(summary.average_active_seconds_30_days)}</div>
          </div>
        </div>
        <div className="ml-auto font-mont text-[10px]" style={{ color: "var(--kd9-text-soft)" }}>
          {summary.generated_at ? `Actualizado ${formatDate(summary.generated_at)}` : ""}
        </div>
        <button type="button" onClick={() => load()} className="kd9-btn-outline px-3 py-2 rounded-lg text-[10px] inline-flex items-center gap-1.5">
          <RefreshCw size={12} /> Actualizar
        </button>
      </div>

      <Filters filters={filters} setFilters={setFilters} />

      <div className="flex items-center justify-between mb-3">
        <div className="font-cinzel text-sm kd9-gold-text">USUARIOS</div>
        <div className="font-mont text-[10px]" style={{ color: "var(--kd9-text-soft)" }}>{Number(data.total || 0).toLocaleString("es-CO")} resultados</div>
      </div>

      {loading ? (
        <div className="kd9-card p-8 text-center font-mont text-sm">Cargando actividad…</div>
      ) : data.items?.length ? (
        <>
          <div className="hidden lg:block overflow-x-auto kd9-card">
            <table className="w-full min-w-[1050px] text-left" data-testid="activity-users-table">
              <thead>
                <tr className="border-b" style={{ borderColor: "var(--kd9-border)" }}>
                  {["Usuario", "Rol", "Estado", "Sesión actual", "7 días", "30 días", "Último inicio", "Última actividad", "Último módulo", ...(canManageSessions ? ["Control"] : [])].map((label) => (
                    <th key={label} className="px-4 py-3 font-mont text-[9px] uppercase tracking-widest" style={{ color: "var(--kd9-text-soft)" }}>{label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.items.map((row) => (
                  <tr key={row.id} className="border-b last:border-0" style={{ borderColor: "var(--kd9-border)" }}>
                    <td className="px-4 py-3 max-w-[250px]"><UserIdentity row={row} /></td>
                    <td className="px-4 py-3 font-mont text-xs">{roleLabel(row)}</td>
                    <td className="px-4 py-3"><StateBadge state={row.activity_state} /></td>
                    <td className="px-4 py-3 font-mono text-xs">{row.activity_state === "offline" ? "—" : formatDuration(row.session_seconds)}</td>
                    <td className="px-4 py-3 font-mono text-xs">{formatDuration(row.active_7_seconds)}</td>
                    <td className="px-4 py-3 font-mono text-xs">{formatDuration(row.active_30_seconds)}</td>
                    <td className="px-4 py-3 font-mont text-[10px]">{formatDate(row.login_at)}</td>
                    <td className="px-4 py-3 font-mont text-[10px]">{formatDate(row.last_activity_at)}</td>
                    <td className="px-4 py-3 font-mont text-xs capitalize">{moduleLabel(row.last_module)}</td>
                    {canManageSessions && (
                      <td className="px-4 py-3">
                        {row.id === user?.id ? (
                          <span className="font-mont text-[9px]" style={{ color: "var(--kd9-text-soft)" }}>Sesión actual</span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => revokeSessions(row)}
                            disabled={revokingUserId === row.id}
                            className="kd9-btn-outline px-2.5 py-2 rounded-lg text-[9px] inline-flex items-center gap-1.5 disabled:opacity-50"
                            data-testid={`revoke-sessions-${row.id}`}
                          >
                            <Power size={11}/>
                            {revokingUserId === row.id ? "Cerrando…" : "Cerrar sesiones"}
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid gap-3 lg:hidden" data-testid="activity-users-mobile">
            {data.items.map((row) => (
              <article key={row.id} className="kd9-card p-4">
                <div className="flex items-start gap-3 justify-between">
                  <UserIdentity row={row} />
                  <StateBadge state={row.activity_state} />
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
                  <div><span className="block font-mont text-[9px] uppercase tracking-widest" style={{ color: "var(--kd9-text-soft)" }}>Rol</span>{roleLabel(row)}</div>
                  <div><span className="block font-mont text-[9px] uppercase tracking-widest" style={{ color: "var(--kd9-text-soft)" }}>Módulo</span>{moduleLabel(row.last_module)}</div>
                  <div><span className="block font-mont text-[9px] uppercase tracking-widest" style={{ color: "var(--kd9-text-soft)" }}>Activo 7d</span>{formatDuration(row.active_7_seconds)}</div>
                  <div><span className="block font-mont text-[9px] uppercase tracking-widest" style={{ color: "var(--kd9-text-soft)" }}>Activo 30d</span>{formatDuration(row.active_30_seconds)}</div>
                </div>
                <div className="mt-3 pt-3 border-t font-mont text-[10px] grid gap-1" style={{ borderColor: "var(--kd9-border)", color: "var(--kd9-text-soft)" }}>
                  <span>Último inicio: {formatDate(row.login_at)}</span>
                  <span>Última actividad: {formatDate(row.last_activity_at)}</span>
                  <span>Sesión actual: {row.activity_state === "offline" ? "—" : formatDuration(row.session_seconds)}</span>
                </div>
                {canManageSessions && row.id !== user?.id && (
                  <button
                    type="button"
                    onClick={() => revokeSessions(row)}
                    disabled={revokingUserId === row.id}
                    className="kd9-btn-outline mt-3 w-full px-3 py-2.5 rounded-lg text-[10px] inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
                    data-testid={`revoke-sessions-mobile-${row.id}`}
                  >
                    <Power size={12}/>
                    {revokingUserId === row.id ? "Cerrando sesiones…" : "Cerrar sesiones activas"}
                  </button>
                )}
              </article>
            ))}
          </div>
        </>
      ) : (
        <div className="kd9-card p-8 text-center font-mont text-sm" style={{ color: "var(--kd9-text-soft)" }}>No hay usuarios que coincidan con los filtros.</div>
      )}
    </PageShell>
  );
};

const DeviceIcon = ({ type }) => type === "mobile" ? <Smartphone size={15}/> : <Monitor size={15}/>;

export const SessionDiagnostics = () => {
  const [filters, setFilters] = useState({ search: "", role: "", state: "", status: "" });
  const [data, setData] = useState({ items: [], total: 0 });
  const [selected, setSelected] = useState(null);
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const params = useMemo(() => {
    const result = { limit: 200 };
    Object.entries(filters).forEach(([key, value]) => {
      if (value) result[key] = value;
    });
    return result;
  }, [filters]);

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    setError("");
    try {
      const response = await api.get("/api/activity/support/users", { params });
      setData(response.data || { items: [], total: 0 });
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    const timer = window.setTimeout(() => load(), 250);
    return () => window.clearTimeout(timer);
  }, [load]);

  useEffect(() => {
    const interval = window.setInterval(() => load({ quiet: true }), 60_000);
    return () => window.clearInterval(interval);
  }, [load]);

  const openHistory = async (row) => {
    setSelected(row);
    setHistory(null);
    setHistoryLoading(true);
    try {
      const response = await api.get(`/api/activity/support/users/${row.id}/sessions`, { params: { limit: 12 } });
      setHistory(response.data);
    } catch (requestError) {
      setError(formatApiError(requestError));
      setSelected(null);
    } finally {
      setHistoryLoading(false);
    }
  };

  return (
    <PageShell
      kicker="Soporte Técnico · Solo diagnóstico"
      title="DIAGNÓSTICO DE SESIONES"
      desc="Consulta técnica de accesos y sesiones para resolver incidencias. Esta vista no incluye retención, finanzas ni decisiones de gobierno."
      testId="session-diagnostics-page"
      badge={<span className="inline-flex items-center gap-2 text-[10px] font-mont uppercase tracking-widest" style={{ color: "#86EFAC" }}><ShieldCheck size={14}/> Solo lectura</span>}
    >
      <div className="mb-5 rounded-2xl border p-4 font-cormorant text-base" style={{ borderColor: "rgba(16,185,129,.3)", background: "rgba(16,185,129,.05)", color: "var(--kd9-text-soft)" }}>
        Soporte puede ver estado de sesión, último acceso, dispositivo, navegador, módulo y motivo de cierre. No puede modificar roles, saldos, permisos ni sesiones desde este panel.
      </div>

      <ErrorBanner error={error} onRetry={() => load()} />
      <Filters filters={filters} setFilters={setFilters} support />

      <div className="flex items-center justify-between mb-3">
        <div className="font-cinzel text-sm kd9-gold-text">SESIONES DE USUARIOS</div>
        <div className="flex items-center gap-3">
          <span className="font-mont text-[10px]" style={{ color: "var(--kd9-text-soft)" }}>{Number(data.total || 0).toLocaleString("es-CO")} resultados</span>
          <button type="button" onClick={() => load()} className="kd9-btn-outline px-3 py-2 rounded-lg text-[10px] inline-flex items-center gap-1.5"><RefreshCw size={12}/> Actualizar</button>
        </div>
      </div>

      {loading ? (
        <div className="kd9-card p-8 text-center font-mont text-sm">Cargando diagnóstico…</div>
      ) : (
        <div className="grid gap-3">
          {(data.items || []).map((row) => (
            <article key={row.id} className="kd9-card p-4" data-testid={`session-user-${row.id}`}>
              <div className="grid gap-4 lg:grid-cols-[minmax(230px,1.4fr)_120px_170px_160px_170px_auto] lg:items-center">
                <UserIdentity row={row} />
                <div>
                  <div className="font-mont text-[9px] uppercase tracking-widest mb-1" style={{ color: "var(--kd9-text-soft)" }}>Estado</div>
                  <StateBadge state={row.activity_state} />
                </div>
                <div>
                  <div className="font-mont text-[9px] uppercase tracking-widest mb-1" style={{ color: "var(--kd9-text-soft)" }}>Última actividad</div>
                  <div className="font-mont text-[10px]">{formatDate(row.last_activity_at)}</div>
                  <div className="font-mont text-[9px] mt-1" style={{ color: "var(--kd9-text-soft)" }}>Inicio: {formatDate(row.login_at)}</div>
                </div>
                <div>
                  <div className="font-mont text-[9px] uppercase tracking-widest mb-1" style={{ color: "var(--kd9-text-soft)" }}>Dispositivo</div>
                  <div className="inline-flex items-center gap-1.5 font-mont text-xs capitalize"><DeviceIcon type={row.device_type}/>{row.device_type || "desconocido"} · {row.browser || "—"}</div>
                </div>
                <div>
                  <div className="font-mont text-[9px] uppercase tracking-widest mb-1" style={{ color: "var(--kd9-text-soft)" }}>Módulo / cierre</div>
                  <div className="font-mont text-xs capitalize">{moduleLabel(row.last_module)}</div>
                  <div className="font-mono text-[9px] mt-0.5" style={{ color: "var(--kd9-text-soft)" }}>{row.last_logout_reason || "Sesión sin cierre registrado"}</div>
                </div>
                <button type="button" onClick={() => openHistory(row)} className="kd9-btn-outline px-3 py-2 rounded-lg text-[10px] inline-flex items-center justify-center gap-1.5">
                  <History size={12}/> Historial
                </button>
              </div>
            </article>
          ))}
          {!data.items?.length && <div className="kd9-card p-8 text-center font-mont text-sm" style={{ color: "var(--kd9-text-soft)" }}>No hay usuarios que coincidan con los filtros.</div>}
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={(event) => { if (event.target === event.currentTarget) setSelected(null); }}>
          <div className="kd9-card w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="session-history-modal">
            <div className="p-5 border-b flex gap-3 items-start" style={{ borderColor: "var(--kd9-border)" }}>
              <History size={20} className="kd9-gold-text mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="font-cinzel">HISTORIAL TÉCNICO DE SESIONES</div>
                <div className="font-mont text-[10px] mt-1 truncate" style={{ color: "var(--kd9-text-soft)" }}>{selected.email}</div>
              </div>
              <button type="button" onClick={() => setSelected(null)} className="p-2 rounded-lg" aria-label="Cerrar historial"><X size={17}/></button>
            </div>
            <div className="p-5 overflow-y-auto">
              {historyLoading ? (
                <div className="py-10 text-center font-mont text-sm">Cargando historial…</div>
              ) : (
                <div className="grid gap-3">
                  {(history?.sessions || []).map((session) => (
                    <div key={session.session_id} className="rounded-xl border p-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4" style={{ borderColor: "var(--kd9-border)" }}>
                      <div>
                        <div className="font-mont text-[9px] uppercase tracking-widest" style={{ color: "var(--kd9-text-soft)" }}>Inicio</div>
                        <div className="font-mont text-[10px] mt-1">{formatDate(session.login_at)}</div>
                      </div>
                      <div>
                        <div className="font-mont text-[9px] uppercase tracking-widest" style={{ color: "var(--kd9-text-soft)" }}>Duración / activo</div>
                        <div className="font-mono text-xs mt-1">{formatDuration(session.session_seconds)} / {formatDuration(session.active_seconds)}</div>
                      </div>
                      <div>
                        <div className="font-mont text-[9px] uppercase tracking-widest" style={{ color: "var(--kd9-text-soft)" }}>Dispositivo</div>
                        <div className="font-mont text-xs mt-1 capitalize">{session.device_type || "—"} · {session.browser || "—"}</div>
                      </div>
                      <div>
                        <div className="font-mont text-[9px] uppercase tracking-widest" style={{ color: "var(--kd9-text-soft)" }}>Cierre</div>
                        <div className="font-mono text-[10px] mt-1">{session.logout_reason || (session.ended_at ? "SIN_MOTIVO" : "ABIERTA")}</div>
                      </div>
                      <div className="md:col-span-2 xl:col-span-4 pt-2 border-t flex flex-wrap gap-x-5 gap-y-1 font-mont text-[10px]" style={{ borderColor: "var(--kd9-border)", color: "var(--kd9-text-soft)" }}>
                        <span>Última actividad: {formatDate(session.last_activity_at)}</span>
                        <span>Módulo: {moduleLabel(session.last_module)}</span>
                        {session.ended_at && <span>Fin: {formatDate(session.ended_at)}</span>}
                      </div>
                    </div>
                  ))}
                  {!history?.sessions?.length && <div className="py-10 text-center font-mont text-sm" style={{ color: "var(--kd9-text-soft)" }}>Este usuario todavía no tiene historial de telemetría.</div>}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
};

export default ActivityCenter;
